import { AccessoryOrderStatus, ACCESSORY_ORDER_STATUSES } from '@/models/order';

/**
 * The admin panel's TWO vocabularies for one stored enum, in one file.
 *
 * The STORED values stay `paid | fulfilling | shipped | delivered | refunded |
 * cancelled`. They are read by the customer-facing order tracker, the Flutter
 * app and the marketing site, and every existing order document carries them —
 * renaming the stored value to match a label would break all of those at once.
 * So: map for display, keep the enum. Anything that goes on the wire (a filter
 * value, a PUT body) must use the KEY, never the label.
 *
 * The two maps differ deliberately. The Orders screens speak the fulfilment
 * vocabulary an operator uses; the Users table was asked for Pending / Running
 * / Shipped (spec v2 §07 #2), which is what a founder scanning a customer list
 * wants to read. They are co-located rather than unified precisely BECAUSE they
 * diverge: three copies of this map used to live in three files, and the moment
 * a status value is added, the copies nobody remembered go stale (CLAUDE.md
 * pitfall #30). Here, adding a value fails to compile until both are updated.
 */
export const ORDER_STATUS_LABELS: Record<AccessoryOrderStatus, string> = {
    paid: 'Paid',
    fulfilling: 'Fulfilling',
    shipped: 'Shipped',
    delivered: 'Delivered',
    refunded: 'Refunded',
    cancelled: 'Cancelled',
};

/** The Users-table wording (spec v2 §07 #2). Same keys, different words. */
export const USER_SHIPMENT_STATUS_LABELS: Record<AccessoryOrderStatus, string> = {
    paid: 'Pending',
    fulfilling: 'Running',
    shipped: 'Shipped',
    delivered: 'Delivered',
    refunded: 'Refunded',
    cancelled: 'Cancelled',
};

/** The options for the Users table's shipment filter, in lifecycle order. */
export const ORDER_STATUS_OPTIONS = ACCESSORY_ORDER_STATUSES.map((value) => ({
    value,
    label: USER_SHIPMENT_STATUS_LABELS[value],
}));

/**
 * Label for a status that may be absent or unrecognised.
 *
 * `latestOrderStatus` is a denormalised string, and a user with no orders has
 * none — which is the common case, not an error. An unknown value is shown
 * as-is rather than blanked, so a status added server-side later degrades to
 * something readable instead of disappearing.
 */
export const orderStatusLabel = (status?: string): string => {
    if (!status) return '—';
    return USER_SHIPMENT_STATUS_LABELS[status as AccessoryOrderStatus] ?? status;
};

/** Label for the Orders screens, tolerating an unknown value the same way. */
export const fulfilmentStatusLabel = (status?: string): string => {
    if (!status) return '—';
    return ORDER_STATUS_LABELS[status as AccessoryOrderStatus] ?? status;
};
